/**
 * VideoSuggestions — Post-optimization interview prep video cards shown in the agent chat.
 *
 * Uses the job title + company from the canvas to generate relevant interview
 * preparation topics, then calls the `youtube-search` edge function to fetch
 * actual video IDs and thumbnails. Videos play WITHIN the app via embedded
 * YouTube iframe — no external redirect.
 */

import { Play, Video, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';

export interface InterviewVideo {
  topic: string;
  reason: string;
  videoId: string | null;
  channel: string | null;
  duration: string | null;
}

interface VideoSuggestionsProps {
  jobTitle: string;
  company?: string;
  className?: string;
}

function buildInterviewTopics(
  jobTitle: string,
  company?: string,
): Array<{ topic: string; reason: string; category: string }> {
  const title = (jobTitle || '').toLowerCase();
  const topics: Array<{ topic: string; reason: string; category: string }> = [];
  const seen = new Set<string>();
  const add = (topic: string, reason: string, category: string) => {
    const key = topic.trim().toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    topics.push({ topic: topic.trim(), reason, category });
  };

  add('Behavioral interview STAR method', 'Essential framework for every round', 'behavioral');
  add('"Tell me about yourself" best answers', 'Opens almost every interview', 'behavioral');

  const co = (company || '').trim();
  if (co && !/^(company|unknown|n\/a|target company)$/i.test(co)) {
    add(`${co} interview process and tips`, `Specific to ${co}`, 'company');
  }

  if (/(frontend|front-end|react|ui engineer|web developer|javascript)/.test(title)) {
    add('Frontend system design interview guide', 'Core to your role', 'role');
  } else if (/(backend|back-end|api|java developer|python developer|node)/.test(title)) {
    add('Backend system design scalable APIs guide', 'Core to your role', 'role');
  } else if (/(full.?stack|fullstack)/.test(title)) {
    add('Full stack developer interview guide', 'Core to your role', 'role');
  } else if (/(data scientist|machine learning|ml engineer|ai engineer)/.test(title)) {
    add('Machine learning interview preparation', 'Core to your role', 'role');
  } else if (/(product manager|product owner|program manager)/.test(title)) {
    add('Product Manager interview complete guide', 'Core to your role', 'role');
  } else if (/(devops|sre|site reliability|cloud engineer)/.test(title)) {
    add('DevOps interview CI/CD pipelines guide', 'Core to your role', 'role');
  } else if (/(data analyst|business analyst|analytics)/.test(title)) {
    add('SQL for data analysts interview guide', 'Core to your role', 'role');
  } else if (/(qa|quality|test engineer|sdet)/.test(title)) {
    add('Software testing interview preparation', 'Core to your role', 'role');
  } else if (/(designer|ux|ui designer)/.test(title)) {
    add('UX design portfolio interview guide', 'Core to your role', 'role');
  } else {
    add(`${jobTitle} interview questions and answers`, 'Role-specific preparation', 'role');
  }

  if (/\b(senior|sr\b|principal|staff|lead)/.test(title)) {
    add('Senior engineer system design interview', 'Expected depth at senior level', 'leadership');
  } else if (/\b(manager|director|head of|vp)/.test(title)) {
    add('Engineering manager interview preparation', 'People management depth required', 'leadership');
  }

  return topics.slice(0, 4);
}

const CURATED_FALLBACK_VIDEOS: InterviewVideo[] = [
  { topic: 'STAR Method Interview Technique', reason: 'Essential behavioral framework', videoId: 'WSbN-0swDgM', channel: 'CareerVidz', duration: '10:42' },
  { topic: 'Top Interview Tips to Get the Job', reason: 'Boost your confidence', videoId: 'HG68Ymazo18', channel: 'Indeed', duration: '9:15' },
  { topic: 'How to Ace Any Job Interview', reason: 'Complete interview guide', videoId: '1mHjMNZZvFo', channel: 'Jeff Su', duration: '12:37' },
  { topic: 'Body Language Tips for Interviews', reason: 'Non-verbal communication matters', videoId: 'PCWVi5pAa30', channel: 'Vanessa Van Edwards', duration: '14:08' },
];

function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

function EmbeddedPlayer({ videoId, onClose }: { videoId: string; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 640,
        margin: '0 auto 12px',
        borderRadius: 10,
        overflow: 'hidden',
        background: '#000',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          background: 'rgba(0,0,0,0.7)',
          border: 'none',
          borderRadius: '50%',
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#fff',
        }}
        aria-label="Close video"
      >
        <X className="w-4 h-4" />
      </button>
      <div style={{ position: 'relative', paddingTop: '56.25%' }}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title="Interview preparation video"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </div>
    </div>
  );
}

export default function VideoSuggestions({ jobTitle, company, className = '' }: VideoSuggestionsProps) {
  const topics = useMemo(() => buildInterviewTopics(jobTitle, company), [jobTitle, company]);
  const [videos, setVideos] = useState<InterviewVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!topics.length) { setLoading(false); return; }
    let cancelled = false;

    const fetchVideos = async () => {
      const results: InterviewVideo[] = [];
      const batchTopics = topics.slice(0, 4);

      for (const t of batchTopics) {
        if (cancelled) return;
        try {
          const { data } = await supabase.functions.invoke<{
            videoIds: string[];
            titles: string[];
            channels: string[];
            durations: string[];
          }>('youtube-search', {
            body: { query: t.topic, maxResults: 1, minDurationMinutes: 3 },
          });
          if (cancelled) return;

          const vid = data?.videoIds?.[0] ?? null;
          results.push({
            topic: t.topic,
            reason: t.reason,
            videoId: vid,
            channel: data?.channels?.[0] ?? null,
            duration: data?.durations?.[0] ?? null,
          });
        } catch {
          results.push({ topic: t.topic, reason: t.reason, videoId: null, channel: null, duration: null });
        }
      }

      if (!cancelled) {
        const validResults = results.filter((r) => r.videoId);
        setVideos(validResults.length > 0 ? validResults : CURATED_FALLBACK_VIDEOS);
        setLoading(false);
      }
    };

    // Timeout: if API takes too long, show curated fallback videos instead
    const timeout = window.setTimeout(() => {
      if (!cancelled) { setLoading(false); setVideos(CURATED_FALLBACK_VIDEOS); }
    }, 8000);

    void fetchVideos().finally(() => window.clearTimeout(timeout));
    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [topics]);

  if (loading) {
    return (
      <div className={`ds-agent-video-suggestions ${className}`}>
        <div className="flex items-center gap-1.5 mb-2" style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ds-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <Video className="w-3 h-3" />
          Loading interview prep videos…
        </div>
        <div className="flex gap-2.5">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse rounded-lg" style={{ width: 180, height: 130, background: 'var(--ds-bg-tinted)' }} />
          ))}
        </div>
      </div>
    );
  }

  // Only show videos that have a valid videoId — don't display empty placeholders
  const validVideos = videos.filter((v) => v.videoId && !brokenIds.has(v.videoId!));
  if (!validVideos.length) return null;

  return (
    <div className={`ds-agent-video-suggestions ${className}`}>
      <div
        className="flex items-center gap-1.5 mb-2"
        style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ds-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        <Video className="w-3 h-3" />
        Prepare for Interview — {jobTitle}
      </div>

      {activeVideoId && (
        <EmbeddedPlayer videoId={activeVideoId} onClose={() => setActiveVideoId(null)} />
      )}

      <div className="flex gap-2.5 overflow-x-auto pb-1" style={{ scrollSnapType: 'x mandatory' }}>
        {validVideos.map((v) => {
          const thumb = v.videoId ? getYouTubeThumbnail(v.videoId) : null;
          const isPlaying = activeVideoId === v.videoId;

          return (
            <button
              key={v.topic}
              type="button"
              onClick={() => {
                if (v.videoId) {
                  setActiveVideoId(isPlaying ? null : v.videoId);
                }
              }}
              className="ds-agent-video-card group shrink-0"
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '180px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: isPlaying
                  ? '2px solid var(--ds-accent, #6366f1)'
                  : '1px solid var(--ds-border)',
                background: 'var(--ds-bg-card)',
                textDecoration: 'none',
                scrollSnapAlign: 'start',
                transition: 'box-shadow 0.15s, border-color 0.15s',
                cursor: v.videoId ? 'pointer' : 'default',
                padding: 0,
                textAlign: 'left',
              }}
            >
              {/* Thumbnail */}
              <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', background: '#000', overflow: 'hidden' }}>
                {thumb ? (
                  <img
                    src={thumb}
                    alt={v.topic}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    loading="lazy"
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (img.naturalWidth <= 120 && v.videoId) {
                        setBrokenIds(prev => new Set([...prev, v.videoId!]));
                      }
                    }}
                    onError={() => {
                      if (v.videoId) setBrokenIds(prev => new Set([...prev, v.videoId!]));
                    }}
                  />
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}>
                    <Play className="w-8 h-8 text-white/60" />
                  </div>
                )}
                {/* Play overlay */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isPlaying ? 'rgba(99,102,241,0.3)' : 'rgba(0,0,0,0.25)',
                    opacity: isPlaying ? 1 : 0,
                    transition: 'opacity 0.15s',
                  }}
                  className={isPlaying ? '' : 'group-hover:!opacity-100'}
                >
                  <Play className="w-8 h-8 text-white drop-shadow" fill="white" />
                </div>
                {/* Duration badge */}
                {v.duration && (
                  <span style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: '9.5px', fontWeight: 600, padding: '1px 4px', borderRadius: '3px' }}>
                    {v.duration}
                  </span>
                )}
                {isPlaying && (
                  <span style={{ position: 'absolute', top: 4, left: 4, background: 'var(--ds-accent, #6366f1)', color: '#fff', fontSize: '8.5px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', textTransform: 'uppercase' }}>
                    Playing
                  </span>
                )}
              </div>

              {/* Text */}
              <div style={{ padding: '6px 8px' }}>
                <div style={{ fontSize: '11.5px', fontWeight: 600, lineHeight: '1.3', color: 'var(--ds-text-body)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {v.topic}
                </div>
                {v.channel && (
                  <div style={{ fontSize: '10px', color: 'var(--ds-text-muted)', marginTop: '2px' }}>
                    {v.channel}
                  </div>
                )}
                <div style={{ fontSize: '10px', color: 'var(--ds-text-muted)', marginTop: '2px' }}>
                  {v.reason}
                </div>
                <div className="flex items-center gap-0.5" style={{ fontSize: '9.5px', color: 'var(--ds-accent)', marginTop: '3px' }}>
                  <Play className="w-2.5 h-2.5" />
                  Play in app
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
